/* LA CHIAVE CON CUI UN BACKTEST E UN TRADEBOT SI RICONOSCONO COME LA STESSA COSA.
 *
 * Sta in `lib/` e non dentro una schermata perché ora la leggono in due:
 * il dettaglio del tradebot (che dal bot risale al backtest) e la lista dei
 * backtest (che dal backtest risale al bot). Due implementazioni divergenti
 * della stessa chiave sono due verdetti diversi sullo stesso bot, e su un bot
 * che opera con denaro vero è il verdetto che conta.
 *
 * Il legame NON passa da un id salvato all'avvio. Un id varrebbe solo per i bot
 * creati d'ora in poi, mentre quelli che esistono adesso — compresi quelli
 * compilati a mano copiando i valori da una riga di ricerca — resterebbero
 * orfani per sempre. Il riconoscimento è sui PARAMETRI: se strategia, coppia,
 * candela e `strategyParams` coincidono, è lo stesso esperimento portato live.
 *
 * Le due normalizzazioni sono la parte che conta, e sono state misurate in
 * produzione: senza di esse il confronto fallisce proprio sul caso che importa
 * di più.
 *
 *  1. TOKEN AVVOLTI. I bot che operano con denaro vero girano su BSC e
 *     comprano `BTCB`; i backtest sono su `BTC`. Senza la mappa, l'unico bot
 *     in funzione con soldi veri non si riconosce.
 *  2. STABLECOIN EQUIPARATE. La strategia non distingue USDT da USDC, ma i bot
 *     sì: uno di quelli esistenti usa la seconda.
 *
 * `chiaveConfig` è identica a quella del frontend attuale
 * (`sem-bot-ui/src/containers/BacktestSearchContainer.js`): quel codice gira in
 * produzione ed è la sola versione verificata contro i bot reali. Qualsiasi
 * modifica qui va confrontata con quella.
 */

/* ==========================================================================
 * Normalizzazioni
 * ======================================================================== */

/** Token avvolti ricondotti al sottostante: `BTCB` su BSC è `BTC` altrove. */
const AVVOLTI: Record<string, string> = {
  BTCB: 'BTC',
  WBTC: 'BTC',
  WBNB: 'BNB',
  WETH: 'ETH',
  WMATIC: 'MATIC',
};

/** Stablecoin intercambiabili: la strategia non le distingue, i bot sì. */
const STABLE = new Set(['USDT', 'USDC', 'BUSD', 'DAI', 'TUSD', 'FDUSD']);

export function normalizzaAsset(a: string | null | undefined): string {
  const su = String(a ?? '').toUpperCase();
  return AVVOLTI[su] ?? su;
}

export function normalizzaValuta(c: string | null | undefined): string {
  const su = String(c ?? '').toUpperCase();
  return STABLE.has(su) ? 'STABLE' : su;
}

/* ==========================================================================
 * La chiave
 * ======================================================================== */

/** Separatore che non può comparire dentro un nome di strategia o di
 *  parametro: senza, `SEM|A` e `SEM` con un parametro `A` darebbero la stessa
 *  chiave. Esportato perché anche chi raggruppa i bot per interrogare il server
 *  ha bisogno di comporre chiavi con lo stesso separatore. */
export const SEP_CHIAVE = '\u0001';

/** Quel che serve per calcolare una chiave: vale sia per un bot sia per una
 *  riga di backtest, che condividono questi cinque campi. */
export interface ConfigurazioneConfrontabile {
  strategyName: string;
  asset: string;
  currency: string;
  candleSize: number;
  strategyParams: Record<string, unknown> | string | null | undefined;
}

/**
 * La chiave con cui un backtest e un tradebot si riconoscono come la stessa
 * cosa. `null` quando i parametri mancano: senza di essi non c'è niente da
 * confrontare, e restituire una chiave parziale accoppierebbe configurazioni
 * diverse fra loro.
 *
 * Le chiavi di `strategyParams` si ordinano perché due JSON con le stesse
 * coppie scritte in ordine diverso descrivono la stessa configurazione.
 */
export function chiaveConfig(o: ConfigurazioneConfrontabile | null | undefined): string | null {
  if (!o) return null;

  let p = o.strategyParams;
  if (typeof p === 'string') {
    try {
      p = JSON.parse(p) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  if (!p || typeof p !== 'object') return null;

  const parametri = p as Record<string, unknown>;
  const ordinati = Object.keys(parametri)
    .sort()
    .map((k) => `${k}=${parametri[k]}`)
    .join('|');

  return [
    o.strategyName,
    normalizzaAsset(o.asset),
    normalizzaValuta(o.currency),
    o.candleSize,
    ordinati,
  ].join(SEP_CHIAVE);
}
